import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { ModalService } from '../../services/modal.service';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { Sample, ExtractionConfig, Attribute, DataType, InferenceRun } from '../../models/truth-management.model';
import { InferenceRunService } from '../../services/inference-run.service';

interface ExtractionDocument {
  id: number;
  documentName: string;
  documentType: string;
  documentCode: string;
  attributes: number;
  samples: number;
  addedDate: Date;
  status: 'Active' | 'Draft' | 'Review';
}

@Component({
  selector: 'app-data-extraction',
  imports: [CommonModule, FormsModule, PdfViewerModule],
  templateUrl: './data-extraction.html',
  styleUrl: './data-extraction.scss',
})
export class DataExtraction implements OnInit {
  // View mode
  viewMode: 'table' | 'configuration' | 'inference' = 'table';
  
  // Extraction documents for table view
  extractionDocuments: ExtractionDocument[] = [];
  selectedExtractionDocument: ExtractionDocument | null = null;
  
  // Dialog for new document
  showNewDocDialog: boolean = false;
  newDoc = {
    documentName: '',
    documentType: '',
    documentCode: ''
  };
  
  // Sample data
  samples: Sample[] = [];
  selectedSample: Sample | null = null;
  currentSampleIndex: number = 0;
  totalSamples: number = 0;

  // PDF Viewer
  pdfSrc: Uint8Array | null = null;
  totalPages: number = 0;
  currentPage: number = 1;
  zoom: number = 1.0;

  // Configuration
  attributes: Attribute[] = [];
  selectedAttributeId: number | null = null;
  selectedDataType: string = '';
  extractionPrompt: string = '';
  
  dataTypes: DataType[] = ['Text', 'Number', 'Date', 'Boolean', 'Currency', 'Percentage', 'Email', 'Phone'];
  
  currentConfig: ExtractionConfig | null = null;
  isSaving: boolean = false;
  activeExtractionTab: 'configuration' | 'inference' = 'configuration';

  // Inference runs
  inferenceRuns: InferenceRun[] = [];
  filteredInferenceRuns: InferenceRun[] = [];
  inferenceSearchQuery: string = '';
  selectedDocTypeFilter: string = '';
  showNewItemDialog: boolean = false;
  selectedDocType: string = '';
  selectedModel: string = '';
  documentTitle: string = '';
  selectedFile: File | null = null;
  isRunning: boolean = false;
  isAuthenticated: boolean = false;

  @ViewChild('fileInputInference') fileInputInference!: ElementRef<HTMLInputElement>;

  constructor(
    public router: Router,
    private http: HttpClient,
    private inferenceRunService: InferenceRunService,
    public authService: AuthService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();
    this.loadExtractionDocuments();
    this.loadSamples();
    this.loadInferenceRuns();
  }

  loadExtractionDocuments(): void {
    // Mock data - replace with actual API call
    this.extractionDocuments = [
      {
        id: 1,
        documentName: 'DTSAUSDU',
        documentType: 'Loan Agreement',
        documentCode: 'LA-001',
        attributes: 47,
        samples: 25,
        addedDate: new Date('2024-12-15'),
        status: 'Active'
      },
      {
        id: 2,
        documentName: 'FNMA Form 1003',
        documentType: 'Application Form',
        documentCode: 'AF-1003',
        attributes: 32,
        samples: 18,
        addedDate: new Date('2024-12-20'),
        status: 'Active'
      },
      {
        id: 3,
        documentName: 'Credit Report',
        documentType: 'Financial Document',
        documentCode: 'FD-CR-01',
        attributes: 65,
        samples: 42,
        addedDate: new Date('2025-01-05'),
        status: 'Review'
      },
      {
        id: 4,
        documentName: 'Property Appraisal',
        documentType: 'Valuation Report',
        documentCode: 'VR-PA-02',
        attributes: 28,
        samples: 15,
        addedDate: new Date('2025-01-08'),
        status: 'Draft'
      }
    ];
  }

  loadSamples(): void {
    this.samples = [];
    this.totalSamples = 0;
  }

  loadInferenceRuns(): void {
    this.inferenceRunService.inferenceRuns$.subscribe(runs => {
      this.inferenceRuns = runs;
      this.filteredInferenceRuns = runs;
    });
  }

  filterInferenceRuns(): void {
    let filtered = this.inferenceRuns;

    // Filter by search query
    if (this.inferenceSearchQuery.trim()) {
      const query = this.inferenceSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(run => 
        run.documentTitle?.toLowerCase().includes(query) || false
      );
    }

    // Filter by document type
    if (this.selectedDocTypeFilter) {
      filtered = filtered.filter(run => 
        run.documentType === this.selectedDocTypeFilter
      );
    }

    this.filteredInferenceRuns = filtered;
  }

  clearInferenceSearch(): void {
    this.inferenceSearchQuery = '';
    this.filterInferenceRuns();
  }

  selectSample(sample: Sample, index: number): void {
    this.selectedSample = sample;
    this.currentSampleIndex = index;
    
    // Load attributes for this sample's document type
    this.loadAttributes();
    
    // Load PDF if available
    if (sample.pdfData) {
      this.pdfSrc = sample.pdfData;
      this.currentPage = sample.firstPage;
    } else {
      // TODO: Load PDF from backend
      this.pdfSrc = null;
    }
    
    // Reset form
    this.resetForm();
  }

  loadAttributes(): void {
    this.attributes = [];
  }

  resetForm(): void {
    this.selectedAttributeId = null;
    this.selectedDataType = '';
    this.extractionPrompt = '';
    this.currentConfig = null;
  }

  saveConfiguration(): void {
    if (!this.selectedSample || !this.selectedAttributeId || !this.selectedDataType || !this.extractionPrompt.trim()) {
      alert('Please fill in all fields');
      return;
    }

    const selectedAttribute = this.attributes.find(attr => attr.id === this.selectedAttributeId);
    if (!selectedAttribute) {
      alert('Selected attribute not found');
      return;
    }

    const config: ExtractionConfig = {
      sampleId: this.selectedSample.id,
      attributeId: this.selectedAttributeId,
      attributeKey: selectedAttribute.attributeKey,
      attributeLabel: selectedAttribute.attributeLabel,
      dataType: this.selectedDataType,
      extractionPrompt: this.extractionPrompt.trim()
    };

    this.isSaving = true;

    // TODO: Implement actual API call to save configuration
    // Simulate API call
    setTimeout(() => {
      this.isSaving = false;
      alert('Configuration saved successfully!');
      this.resetForm();
    }, 500);
  }

  // PDF Viewer methods
  onPdfLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  zoomIn(): void {
    if (this.zoom < 2.0) {
      this.zoom += 0.1;
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom -= 0.1;
    }
  }

  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  navigateToTruthManagement(): void {
    this.router.navigate(['/truth-management']);
  }

  navigateToExtractionEval(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  setExtractionTab(tab: 'configuration' | 'inference'): void {
    this.activeExtractionTab = tab;
    
    if (tab === 'configuration') {
      // Show table view for configuration tab
      this.viewMode = 'table';
    } else if (tab === 'inference') {
      // Show inference view for inference tab
      this.viewMode = 'configuration';
    }
  }

  // Inference dialog methods
  openNewItemDialog(): void {
    this.showNewItemDialog = true;
  }

  closeNewItemDialog(): void {
    this.showNewItemDialog = false;
    this.selectedDocType = '';
    this.selectedModel = '';
    this.documentTitle = '';
    this.selectedFile = null;
    if (this.fileInputInference) {
      this.fileInputInference.nativeElement.value = '';
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      this.selectedFile = file;
    } else {
      alert('Please select a valid PDF file');
      event.target.value = '';
    }
  }

  executeRun(): void {
    if (!this.selectedDocType || !this.selectedModel || !this.documentTitle || !this.selectedFile) {
      return;
    }

    this.isRunning = true;

    // Create new inference run immediately (without reading file for now to avoid hanging)
    const newRun: InferenceRun = {
      id: this.inferenceRunService.getNextId(),
      documentType: this.selectedDocType,
      documentTitle: this.documentTitle,
      modelName: this.selectedModel,
      fileName: this.selectedFile!.name,
      fileData: undefined, // Skip file data for now to prevent hanging
      samples: 8,
      timeOfRun: new Date(),
      progress: 0,
      status: 'Pending',
      createdDate: new Date()
    };

    // Add to service
    this.inferenceRunService.addRun(newRun);
    
    // Reset form and close dialog
    this.closeNewItemDialog();
    this.isRunning = false;

    // Start progress simulation
    setTimeout(() => {
      this.simulateProgress(newRun.id);
    }, 100);
  }

  simulateProgress(runId: number): void {
    this.inferenceRunService.updateRunProgress(runId, 0, 'Running');

    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 10 + 5; // Progress between 5-15% each time
      if (progress >= 100) {
        progress = 100;
        this.inferenceRunService.updateRunProgress(runId, 100, 'Completed');
        clearInterval(interval);
      } else {
        this.inferenceRunService.updateRunProgress(runId, Math.floor(progress), 'Running');
      }
    }, 800); // Slower interval for better visibility
  }

  navigateToInferenceResults(runId: number): void {
    const run = this.inferenceRunService.getRunById(runId);
    this.router.navigate(['/inference-results', runId], {
      state: { runData: run }
    });
  }

  // Table view methods
  onRowDoubleClick(doc: ExtractionDocument): void {
    this.selectedExtractionDocument = doc;
    this.viewMode = 'configuration';
  }

  backToTable(): void {
    this.viewMode = 'table';
    this.selectedExtractionDocument = null;
  }

  openNewDocDialog(): void {
    this.showNewDocDialog = true;
  }

  closeNewDocDialog(): void {
    this.showNewDocDialog = false;
    this.newDoc = {
      documentName: '',
      documentType: '',
      documentCode: ''
    };
  }

  addNewDocument(): void {
    if (!this.newDoc.documentName || !this.newDoc.documentType || !this.newDoc.documentCode) {
      return;
    }

    const newDocument: ExtractionDocument = {
      id: this.extractionDocuments.length + 1,
      documentName: this.newDoc.documentName,
      documentType: this.newDoc.documentType,
      documentCode: this.newDoc.documentCode,
      attributes: 0,
      samples: 0,
      addedDate: new Date(),
      status: 'Draft'
    };

    this.extractionDocuments.push(newDocument);
    this.closeNewDocDialog();
  }

  logout(): void {
    // Show logout confirmation modal (Scenario 3)
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }
}
